

/// <reference path="../../leaflet.markercluster.d.ts" />
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import {
  MapContainer,
  Marker,
  Polygon,
  Popup,
  TileLayer,
  Tooltip,
  Circle,
  CircleMarker,
  useMap,
  useMapEvents,
  LayersControl,
  LayerGroup,
  GeoJSON,
} from 'react-leaflet';
import type { MapContainerProps } from 'react-leaflet';
import React from 'react';
// Styles for geolocation control and radius tooltip
const geolocationControlStyles = `
  .leaflet-control-geolocation {
    background-color: white;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    transition: all 0.2s;
  }
  .leaflet-control-geolocation:hover {
    background-color: #f4f4f4;
    border-color: rgba(0,0,0,0.3);
  }
  .leaflet-control-geolocation svg {
    color: #333;
  }
  
  .radius-tooltip {
    background-color: rgba(255, 255, 255, 0.95) !important;
    border: 2px solid #3b82f6 !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
    padding: 8px 12px !important;
    font-size: 12px !important;
  }
  
  .radius-tooltip::before {
    display: none !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = geolocationControlStyles;
  if (!document.head.querySelector('style[data-leaflet-geolocation]')) {
    styleElement.setAttribute('data-leaflet-geolocation', 'true');
    document.head.appendChild(styleElement);
  }
}

import type {
  BaseMapProps,
  MapCoordinates,
  MapMarkerData,
  MapPolygonData,
} from '../features/inventaires/map-view/providers';
import { MapInfoWindowContent } from '../features/inventaires/map-view/providers/map-info-window-content';

const DEFAULT_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const GOOGLE_MAP_TILE_URL = 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}';
const GOOGLE_SATELLITE_TILE_URL = 'https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}';
const GOOGLE_SUBDOMAINS = ['mt0', 'mt1', 'mt2', 'mt3'];

export interface LeafletMapHandle {
  invalidateSize: () => void;
  setView: (center: { lat: number; lng: number }, zoom: number) => void;
}

export interface GeoJSONLayerOptions {
  url?: string;
  data?: GeoJSON.FeatureCollection | GeoJSON.Feature;
  style?: L.PathOptions | ((feature: GeoJSON.Feature) => L.PathOptions);
  onEachFeature?: (feature: GeoJSON.Feature, layer: L.Layer) => void;
  pointToLayer?: (feature: GeoJSON.Feature, latlng: L.LatLng) => L.Layer;
  name?: string;
  checked?: boolean;
}

function useFixLeafletIcons(): void {
  useEffect(() => {
    const prototype = L.Icon.Default.prototype as L.Icon<L.IconOptions> & {
      _getIconUrl?: () => string;
    };
    delete prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    });
  }, []);
}

interface GeolocationControlProps {
  onLocationFound?: (coords: MapCoordinates) => void;
}

// Custom Geolocation Control Component
function GeolocationControl({ onLocationFound }: GeolocationControlProps) {
  const map = useMap();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
    const button = L.DomUtil.create('a', 'leaflet-control-geolocation', container);
    button.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"></path>
        <circle cx="12" cy="9" r="3"></circle>
      </svg>
    `;
    button.href = '#';
    button.title = 'Me localiser';
    button.setAttribute('role', 'button');
    button.setAttribute('aria-label', 'Me localiser');

    L.DomEvent.disableClickPropagation(button);

    const handleClick = (e: Event) => {
      L.DomEvent.stopPropagation(e);
      if (isLoading) return;

      setIsLoading(true);
      const buttonEl = e.target as HTMLElement;
      buttonEl.style.opacity = '0.6';
      buttonEl.style.cursor = 'wait';

      if (!navigator.geolocation) {
        alert('La géolocalisation n\'est pas supportée par votre navigateur.');
        setIsLoading(false);
        buttonEl.style.opacity = '1';
        buttonEl.style.cursor = 'pointer';
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const latlng: L.LatLngExpression = [latitude, longitude];

          const currentZoom = map.getZoom();
          map.setView(latlng, currentZoom);

          onLocationFound?.({ lat: latitude, lng: longitude });

          setIsLoading(false);
          buttonEl.style.opacity = '1';
          buttonEl.style.cursor = 'pointer';
        },
        (error) => {
          let message = 'Erreur de géolocalisation';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Permission de géolocalisation refusée';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Position indisponible';
              break;
            case error.TIMEOUT:
              message = 'Délai d\'attente dépassé';
              break;
          }
          alert(message);
          setIsLoading(false);
          buttonEl.style.opacity = '1';
          buttonEl.style.cursor = 'pointer';
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    };

    L.DomEvent.on(button, 'click', handleClick);

    const control = new L.Control({ position: 'topleft' });
    control.onAdd = () => container;
    control.addTo(map);

    return () => {
      control.remove();
    };
  }, [map, isLoading, onLocationFound]);

  return null;
}

// Map Controller Component
function MapController({
  center,
  zoom,
  eventHandlers,
  userInteractedRef,
  isInitializedRef,
  forceCenterRevision = 0,
}: {
  center: { lat: number; lng: number };
  zoom: number;
  eventHandlers?: BaseMapProps['eventHandlers'];
  userInteractedRef: React.MutableRefObject<boolean>;
  isInitializedRef: React.MutableRefObject<boolean>;
  forceCenterRevision?: number;
}) {
  const map = useMap();
  const prevForceRevisionRef = useRef(0);

  useMapEvents({
    click: (event) => {
      eventHandlers?.onMapClick?.({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
    zoomstart: () => {
      userInteractedRef.current = true;
    },
    dragstart: () => {
      userInteractedRef.current = true;
    },
    zoomend: () => {
      if (eventHandlers?.onMapMove) {
        const mapCenter = map.getCenter();
        eventHandlers.onMapMove({ lat: mapCenter.lat, lng: mapCenter.lng }, map.getZoom());
      }
    },
    moveend: () => {
      if (eventHandlers?.onMapMove) {
        const mapCenter = map.getCenter();
        eventHandlers.onMapMove({ lat: mapCenter.lat, lng: mapCenter.lng }, map.getZoom());
      }
      if (eventHandlers?.onBoundsChanged) {
        const bounds = map.getBounds();
        eventHandlers.onBoundsChanged({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
      }
    },
    resize: () => {
      map.invalidateSize();
    },
  });

  useEffect(() => {
    isInitializedRef.current = true;
  }, [isInitializedRef]);

  useEffect(() => {
    if (!isInitializedRef.current) {
      return;
    }

    try {
      map.invalidateSize();
    } catch { }

    const shouldForceCenter =
      forceCenterRevision > 0 && forceCenterRevision !== prevForceRevisionRef.current;

    if (shouldForceCenter) {
      prevForceRevisionRef.current = forceCenterRevision;
      userInteractedRef.current = false;
      const currentZoom = map.getZoom();
      map.setView([center.lat, center.lng], currentZoom);
      return;
    }

    if (center) {
      const currentCenter = map.getCenter();
      const distance = currentCenter.distanceTo([center.lat, center.lng]);
      const threshold = 1000;

      if (userInteractedRef.current) {
        if (distance > threshold) {
          const currentZoom = map.getZoom();
          map.setView([center.lat, center.lng], currentZoom);
        }
      } else {
        const currentZoom = map.getZoom();
        map.setView([center.lat, center.lng], currentZoom);
      }
    }
  }, [center, map, userInteractedRef, isInitializedRef, forceCenterRevision]);

  return null;
}


// Individual Marker Component
function LeafletMarker({
  marker,
  eventHandlers,
  renderInfoWindow,
  onViewDetails,
}: {
  marker: MapMarkerData;
  eventHandlers?: BaseMapProps['eventHandlers'];
  renderInfoWindow?: BaseMapProps['renderInfoWindow'];
  onViewDetails?: (inventaireId: string) => void;
}) {
  const markerIcon =
    typeof marker.data?.icon === 'string'
      ? L.icon({
        iconUrl: marker.data.icon,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
        popupAnchor: [0, -24],
        className: 'leaflet-custom-marker',
      })
      : undefined;

  const handleClick = useCallback(() => {
    eventHandlers?.onMarkerClick?.(marker);
  }, [marker, eventHandlers]);

  return (
    <Marker
      position={[marker.position.lat, marker.position.lng]}
      icon={markerIcon}
    //eventHandlers={{ click: handleClick }}
    >
      {marker.title && (
        <Tooltip permanent={false}>{marker.title}</Tooltip>
      )}
      {(marker.title || marker.description || renderInfoWindow) && (
        <Popup className="leaflet-custom-popup" maxWidth={320} minWidth={250}>
          {renderInfoWindow ? (
            renderInfoWindow(marker)
          ) : (
            <MapInfoWindowContent marker={marker} onViewDetails={onViewDetails} />
          )}
        </Popup>
      )}
    </Marker>
  );
}

// Marker Cluster Wrapper Component
function MarkerClusterWrapper({
  markers,
  clustering,
  eventHandlers,
  renderInfoWindow,
  onViewDetails,
}: {
  markers: MapMarkerData[];
  clustering?: BaseMapProps['clustering'];
  eventHandlers?: BaseMapProps['eventHandlers'];
  renderInfoWindow?: BaseMapProps['renderInfoWindow'];
  onViewDetails?: (inventaireId: string) => void;
}) {
  const map = useMap();
  const clusterRef = useRef<L.LayerGroup | null>(null);
  const markersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    const initCluster = async () => {
      // Clear existing markers
      markersRef.current.forEach((marker) => {
        if (clusterRef.current) {
          clusterRef.current.removeLayer(marker);
        }
        marker.remove();
      });
      markersRef.current = [];

      if (clusterRef.current && map.hasLayer(clusterRef.current)) {
        map.removeLayer(clusterRef.current);
      }

      const hasClusterPlugin = typeof (L as any).markerClusterGroup === 'function';
      if (!hasClusterPlugin) {
        try {
          // @ts-expect-error - leaflet.markercluster types are defined in leaflet.markercluster.d.ts
          await import('leaflet.markercluster');
        } catch {
          // Fallback: render markers without clustering
          markers.forEach((markerData) => {
            const markerIcon =
              typeof markerData.data?.icon === 'string'
                ? L.icon({
                  iconUrl: markerData.data.icon,
                  iconSize: [30, 30],
                  iconAnchor: [15, 30],
                  popupAnchor: [0, -24],
                  className: 'leaflet-custom-marker',
                })
                : undefined;

            const leafletMarker = L.marker([markerData.position.lat, markerData.position.lng], {
              icon: markerIcon,
            });

            if (markerData.title) {
              leafletMarker.bindTooltip(markerData.title, { permanent: false });
            }

            //leafletMarker.on('click', () => {
            //  eventHandlers?.onMarkerClick?.(markerData);
            //});

            leafletMarker.addTo(map);
            markersRef.current.push(leafletMarker);
          });
          return;
        }
      }

      if (typeof (L as any).markerClusterGroup === 'function') {
        const clusterLayer = (L as any).markerClusterGroup({
          maxClusterRadius: clustering?.maxClusterRadius ?? 40,
          spiderfyOnMaxZoom: clustering?.spiderfyOnMaxZoom ?? true,
          showCoverageOnHover: clustering?.showCoverageOnHover ?? false,
        });

        clusterRef.current = clusterLayer;
        map.addLayer(clusterLayer);

        markers.forEach((markerData) => {
          const markerIcon =
            typeof markerData.data?.icon === 'string'
              ? L.icon({
                iconUrl: markerData.data.icon,
                iconSize: [30, 30],
                iconAnchor: [15, 30],
                popupAnchor: [0, -24],
                className: 'leaflet-custom-marker',
              })
              : undefined;

          const leafletMarker = L.marker([markerData.position.lat, markerData.position.lng], {
            icon: markerIcon,
          });

          if (markerData.title) {
            leafletMarker.bindTooltip(markerData.title, { permanent: false });
          }

          // Create popup content for clustered markers
          // Note: For React components in popups, we'll handle it when the popup opens
          if (markerData.title || markerData.description || renderInfoWindow) {
            const popupContainer = document.createElement('div');
            popupContainer.className = '';

            // Store marker data for popup rendering
            (leafletMarker as any).__markerData = markerData;
            (leafletMarker as any).__renderInfoWindow = renderInfoWindow;
            (leafletMarker as any).__onViewDetails = onViewDetails;

            leafletMarker.bindPopup(popupContainer, {
              className: 'leaflet-custom-popup',
              maxWidth: 320,
              minWidth: 250,
            });

            // Render popup content when it opens
            leafletMarker.on('popupopen', async () => {
              const { createRoot } = await import('react-dom/client');
              const React = await import('react');
              const root = createRoot(popupContainer);

              const content = (leafletMarker as any).__renderInfoWindow
                ? (leafletMarker as any).__renderInfoWindow((leafletMarker as any).__markerData)
                : React.createElement(MapInfoWindowContent, {
                  marker: (leafletMarker as any).__markerData,
                  onViewDetails: (leafletMarker as any).__onViewDetails,
                });

              root.render(content);
            });

            leafletMarker.on('popupclose', () => {
              // Cleanup is handled automatically by React 18
            });
          }

          leafletMarker.on('click', () => {
            // eventHandlers?.onMarkerClick?.(markerData);
          });

          clusterLayer.addLayer(leafletMarker);
          markersRef.current.push(leafletMarker);
        });
      }
    };

    void initCluster();

    return () => {
      markersRef.current.forEach((marker) => {
        if (clusterRef.current) {
          clusterRef.current.removeLayer(marker);
        }
        marker.remove();
      });
      markersRef.current = [];

      if (clusterRef.current && map.hasLayer(clusterRef.current)) {
        map.removeLayer(clusterRef.current);
      }
      clusterRef.current = null;
    };
  }, [map, markers, clustering, eventHandlers]);

  // Markers are managed natively by the cluster, so we don't render React components
  // This prevents duplication
  return null;
}

// Markers Layer Component
function LeafletMarkersLayer({
  markers,
  clustering,
  eventHandlers,
  renderInfoWindow,
  onViewDetails,
}: {
  markers: MapMarkerData[];
  clustering?: BaseMapProps['clustering'];
  eventHandlers?: BaseMapProps['eventHandlers'];
  renderInfoWindow?: BaseMapProps['renderInfoWindow'];
  onViewDetails?: (inventaireId: string) => void;
}) {
  const useCluster = clustering?.enabled ?? false;

  if (useCluster) {
    return (
      <MarkerClusterWrapper
        markers={markers}
        clustering={clustering}
        eventHandlers={eventHandlers}
        renderInfoWindow={renderInfoWindow}
        onViewDetails={onViewDetails}
      />
    );
  }

  return (
    <>
      {markers.map((marker) => (
        <LeafletMarker
          key={marker.id}
          marker={marker}
          eventHandlers={eventHandlers}
          renderInfoWindow={renderInfoWindow}
          onViewDetails={onViewDetails}
        />
      ))}
    </>
  );
}

// Create hatched pattern helper
function createHatchedPattern(color: string = 'yellow', id: string = 'hatched-pattern'): void {
  if (typeof document !== 'undefined' && document.getElementById(id)) {
    return;
  }

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '0');
  svg.setAttribute('height', '0');
  svg.style.position = 'absolute';
  svg.style.width = '0';
  svg.style.height = '0';

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
  pattern.setAttribute('id', id);
  pattern.setAttribute('patternUnits', 'userSpaceOnUse');
  pattern.setAttribute('width', '8');
  pattern.setAttribute('height', '8');

  const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  line1.setAttribute('x1', '0');
  line1.setAttribute('y1', '0');
  line1.setAttribute('x2', '8');
  line1.setAttribute('y2', '8');
  line1.setAttribute('stroke', color);
  line1.setAttribute('stroke-width', '1');
  line1.setAttribute('opacity', '0.6');

  pattern.appendChild(line1);
  defs.appendChild(pattern);
  svg.appendChild(defs);

  if (typeof document !== 'undefined') {
    document.body.appendChild(svg);
  }
}

// Polygon Component with hatched pattern support
function LeafletPolygon({
  polygon,
  eventHandlers,
  renderInfoWindow,
  onViewDetails,
}: {
  polygon: MapPolygonData;
  eventHandlers?: BaseMapProps['eventHandlers'];
  renderInfoWindow?: BaseMapProps['renderInfoWindow'];
  onViewDetails?: (inventaireId: string) => void;
}) {
  const pathOptions: L.PathOptions = {
    color: polygon.strokeColor || '#15803d',
    weight: polygon.strokeWeight ?? 2,
    opacity: polygon.strokeOpacity ?? 1,
    fillColor: polygon.fillColor || '#22c55e',
    fillOpacity: polygon.fillOpacity ?? 0.2,
  };

  useEffect(() => {
    if (polygon.useHatchedPattern) {
      const patternColor = polygon.hatchedPatternColor || polygon.fillColor || 'yellow';
      const patternId = `hatched-pattern-${polygon.id}`;

      createHatchedPattern(patternColor, patternId);

      const timer = setTimeout(() => {
        const svgs = document.querySelectorAll('svg');
        svgs.forEach((svg) => {
          const paths = svg.querySelectorAll('path');
          paths.forEach((path) => {
            const pathElement = path as SVGPathElement;
            const fillAttr = pathElement.getAttribute('fill');
            if (fillAttr && (
              fillAttr === polygon.fillColor ||
              fillAttr.includes('rgb') ||
              fillAttr === '#ffff00' || fillAttr === 'yellow'
            )) {
              if (!fillAttr.startsWith('url(')) {
                pathElement.setAttribute('fill', `url(#${patternId})`);
                pathElement.setAttribute('fill-opacity', String(polygon.fillOpacity ?? 0.3));
              }
            }
          });
        });
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [polygon]);

  const handleClick = useCallback(() => {
    eventHandlers?.onPolygonClick?.(polygon);
  }, [polygon, eventHandlers]);

  const positions = polygon.coordinates.map((coord: any) => [coord.lat, coord.lng] as [number, number]);

  const popupPosition = polygon.center || (() => {
    const coords = polygon.coordinates;
    const center = coords.reduce(
      (acc: any, coord: any) => ({
        lat: acc.lat + coord.lat,
        lng: acc.lng + coord.lng,
      }),
      { lat: 0, lng: 0 }
    );
    return {
      lat: center.lat / coords.length,
      lng: center.lng / coords.length,
    };
  })();

  const markerData: MapMarkerData = {
    id: polygon.id,
    position: popupPosition,
    title: polygon.title,
    description: polygon.description,
    type: 'epaysage',
    data: polygon.data,
  };

  return (
    <Polygon
      pathOptions={pathOptions}
      positions={positions}
    //eventHandlers={{ click: handleClick }}
    >
      {(polygon.title || polygon.description || polygon.data) && (
        <Popup position={[popupPosition.lat, popupPosition.lng]}>
          {renderInfoWindow ? (
            renderInfoWindow(markerData)
          ) : (
            <div className="min-w-[250px] max-w-[280px] rounded-lg overflow-hidden shadow-lg bg-card">
              <div className="p-3 bg-card space-y-2">
                {polygon.title && (
                  <h3 className="font-semibold text-lg text-foreground">{polygon.title}</h3>
                )}
                {polygon.data?.epaysage?.type_espace && (
                  <p className="text-sm text-foreground">Type: {polygon.data.epaysage.type_espace}</p>
                )}
                {polygon.data?.epaysage?.nom_espace && (
                  <p className="text-sm text-foreground">Nom: {polygon.data.epaysage.nom_espace}</p>
                )}
                {polygon.description && (
                  <p className="text-sm text-muted-foreground">{polygon.description}</p>
                )}
                {polygon.data?.address && (
                  <p className="text-sm text-muted-foreground">Adresse: {polygon.data.address}</p>
                )}
                {polygon.data?.updated_at && (
                  <p className="text-xs text-muted-foreground">
                    Mise à jour le {new Date(polygon.data.updated_at).toLocaleDateString('fr-FR')}
                  </p>
                )}
                {polygon.data?.id && onViewDetails && (
                  <div className="pt-2 border-t border-border">
                    <button
                      type="button"
                      onClick={() => onViewDetails(polygon.data!.id)}
                      className="w-full bg-primary hover:bg-primary text-primary-foreground text-xs py-2 px-3 rounded-md transition-colors duration-200"
                    >
                      Voir plus
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Popup>
      )}
    </Polygon>
  );
}

// Polygons Layer Component
function LeafletPolygonsLayer({
  polygons,
  eventHandlers,
  renderInfoWindow,
  onViewDetails,
}: {
  polygons: MapPolygonData[];
  eventHandlers?: BaseMapProps['eventHandlers'];
  renderInfoWindow?: BaseMapProps['renderInfoWindow'];
  onViewDetails?: (inventaireId: string) => void;
}) {
  return (
    <LayerGroup>
      {polygons.map((polygon) => (
        <LeafletPolygon
          key={polygon.id}
          polygon={polygon}
          eventHandlers={eventHandlers}
          renderInfoWindow={renderInfoWindow}
          onViewDetails={onViewDetails}
        />
      ))}
    </LayerGroup>
  );
}

// GeoJSON Layer Component
function GeoJSONLayer({
  url,
  data,
  style,
  onEachFeature,
  pointToLayer,
}: {
  url?: string;
  data?: GeoJSON.FeatureCollection | GeoJSON.Feature;
  style?: L.PathOptions | ((feature?: GeoJSON.Feature) => L.PathOptions);
  onEachFeature?: (feature: GeoJSON.Feature, layer: L.Layer) => void;
  pointToLayer?: (feature: GeoJSON.Feature, latlng: L.LatLng) => L.Layer;
}) {
  const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection | GeoJSON.Feature | null>(data || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (url && !data) {
      setLoading(true);
      fetch(url)
        .then((response) => response.json())
        .then((json: GeoJSON.FeatureCollection | GeoJSON.Feature) => {
          setGeoJsonData(json);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error loading GeoJSON:', error);
          setLoading(false);
        });
    }
  }, [url, data]);

  if (loading) {
    return null;
  }

  if (!geoJsonData) {
    return null;
  }

  const defaultStyle: L.PathOptions = {
    color: '#3388ff',
    weight: 2,
    opacity: 0.8,
    fillColor: '#3388ff',
    fillOpacity: 0.2,
  };

  const defaultOnEachFeature = (feature: GeoJSON.Feature, layer: L.Layer) => {
    if (feature.properties) {
      const props = feature.properties;
      const name = props.nom || props.name || props.NOM || props.NAME || 'Zone';
      layer.bindTooltip(name, { permanent: false });

      if (Object.keys(props).length > 0) {
        const popupContent = Object.entries(props)
          .map(([key, value]) => `<strong>${key}:</strong> ${value}`)
          .join('<br/>');
        layer.bindPopup(popupContent);
      }
    }
  };

  return (
    <GeoJSON
      data={geoJsonData}
      style={style || defaultStyle}
      onEachFeature={onEachFeature || defaultOnEachFeature}
      pointToLayer={pointToLayer}
    />
  );
}

// Map Handle Component - exposes imperative handle methods
function MapHandle({
  ref,
  userInteractedRef,
}: {
  ref: React.ForwardedRef<LeafletMapHandle>;
  userInteractedRef: React.MutableRefObject<boolean>;
}) {
  const map = useMap();

  useImperativeHandle(
    ref,
    () => ({
      invalidateSize: () => {
        try {
          map.invalidateSize();
        } catch { }
      },
      setView: (center, zoom) => {
        try {
          map.setView([center.lat, center.lng], zoom);
        } catch { }
      },
    }),
    [map]
  );

  return null;
}

export interface LeafletMapProps extends BaseMapProps {
  geoJsonLayers?: GeoJSONLayerOptions[];
  renderMapOverlay?: ReactNode | (() => ReactNode);
  searchRadius?: number;
  searchCenter?: MapCoordinates;
  showRadiusCircle?: boolean;
  forceCenterRevision?: number;
  userLocation?: MapCoordinates | null;
}

export const LeafletMap = forwardRef<LeafletMapHandle, LeafletMapProps>(function LeafletMap(
  {
    options,
    markers = [],
    polygons = [],
    clustering,
    eventHandlers,
    renderInfoWindow,
    className,
    style,
    geoJsonLayers = [],
    renderMapOverlay,
    searchRadius,
    searchCenter,
    showRadiusCircle = false,
    forceCenterRevision = 0,
    userLocation: userLocationProp = null,
  },
  ref
) {
  useFixLeafletIcons();

  const userInteractedRef = useRef<boolean>(false);
  const isInitializedRef = useRef<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<MapCoordinates | null>(null);
  const [hasCenteredOnLocation, setHasCenteredOnLocation] = useState<boolean>(false);

  const handleViewDetails = useCallback(
    (inventaireId: string) => {
      if (eventHandlers?.onViewDetails) {
        eventHandlers.onViewDetails(inventaireId);
      }
    },
    [eventHandlers]
  );

  const handleLocationFound = useCallback(
    (coords: MapCoordinates) => {
      setCurrentLocation(coords);
      // if (mapRef.current && !hasCenteredOnLocation) {
      //   mapRef.current.setView([coords.lat, coords.lng], mapRef.current.getZoom());
      //   setHasCenteredOnLocation(true);
      // }
    },
    [hasCenteredOnLocation]
  );

  useEffect(() => {
    if (!currentLocation && !hasCenteredOnLocation && typeof navigator !== 'undefined') {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          handleLocationFound({ lat: position.coords.latitude, lng: position.coords.longitude });
        },
        (error) => {
          console.warn('Utilisateur refusé la géolocalisation par défaut.', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }
  }, [currentLocation, handleLocationFound, hasCenteredOnLocation]);

  const mapContainerProps: MapContainerProps = {
    center: [options.center.lat, options.center.lng],
    zoom: options.zoom,
    zoomControl: options.zoomControl !== false,
    scrollWheelZoom: options.scrollWheelZoom !== false,
    dragging: options.dragging !== false,
    style: { width: '100%', height: '100%', ...style },
    className: className,
  };

  const wrapperClassName = ['relative', className].filter(Boolean).join(' ');

  const overlayNode =
    typeof renderMapOverlay === 'function' ? renderMapOverlay() : renderMapOverlay;

  return (
    <div className={wrapperClassName} style={{ width: '100%', height: '100%', ...style }}>
      <MapContainer {...mapContainerProps}>
        <MapHandle ref={ref} userInteractedRef={userInteractedRef} />

        <LayersControl position="topleft" collapsed={false}>
          <LayersControl.BaseLayer name="Plan standard (OSM)">
            <TileLayer
              attribution="© OpenStreetMap contributors"
              url={DEFAULT_TILE_URL}
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Google Maps" checked>
            <TileLayer
              attribution="© Google"
              url={GOOGLE_MAP_TILE_URL}
              subdomains={GOOGLE_SUBDOMAINS}
              maxZoom={20}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Google Satellite">
            <TileLayer
              attribution="© Google"
              url={GOOGLE_SATELLITE_TILE_URL}
              subdomains={GOOGLE_SUBDOMAINS}
              maxZoom={20}
            />
          </LayersControl.BaseLayer>

          <LayersControl.Overlay name="Marqueurs" checked>
            <LeafletMarkersLayer
              markers={markers}
              clustering={clustering}
              eventHandlers={eventHandlers}
              renderInfoWindow={renderInfoWindow}
              onViewDetails={handleViewDetails}
            />
          </LayersControl.Overlay>

          <LayersControl.Overlay name="Zones" checked>
            <LeafletPolygonsLayer
              polygons={polygons}
              eventHandlers={eventHandlers}
              renderInfoWindow={renderInfoWindow}
              onViewDetails={handleViewDetails}
            />
          </LayersControl.Overlay>

          {geoJsonLayers.map((geoJsonLayer, index) => (
            <LayersControl.Overlay
              key={index}
              name={geoJsonLayer.name || `GeoJSON ${index + 1}`}
              checked={geoJsonLayer.checked !== false}
            >
              <GeoJSONLayer
                url={geoJsonLayer.url}
                data={geoJsonLayer.data}
                style={geoJsonLayer.style as any}
                onEachFeature={geoJsonLayer.onEachFeature}
                pointToLayer={geoJsonLayer.pointToLayer}
              />
            </LayersControl.Overlay>
          ))}
        </LayersControl>

        {/* Radius Circle Overlay */}
        {showRadiusCircle && searchRadius && searchCenter && (() => {
          // Calculate a point on the circle's perimeter (at the top-right, 45 degrees)
          const radiusInDegrees = searchRadius / 111320; // Convert meters to degrees (approximate)
          const angle = 45 * (Math.PI / 180); // 45 degrees in radians
          const labelLat = searchCenter.lat + radiusInDegrees * Math.cos(angle);
          const labelLng = searchCenter.lng + radiusInDegrees * Math.sin(angle) / Math.cos(searchCenter.lat * Math.PI / 180);

          return (
            <>
              <Circle
                center={[searchCenter.lat, searchCenter.lng]}
                radius={searchRadius}
                pathOptions={{
                  color: '#3b82f6',
                  fillColor: '#3b82f6',
                  fillOpacity: 0.1,
                  weight: 2,
                  dashArray: '5, 10'
                }}
              />
              <CircleMarker
                center={[labelLat, labelLng]}
                radius={0}
                pathOptions={{ opacity: 0, fillOpacity: 0 }}
              >
                <Tooltip permanent direction="right" className="radius-tooltip" offset={[10, 0]}>
                  <div className="text-center">
                    <div className="font-bold text-blue-600">
                      Rayon: {(searchRadius / 1000).toFixed(1)} km
                    </div>
                    <div className="text-xs text-gray-600">
                      Diamètre: {((searchRadius * 2) / 1000).toFixed(1)} km
                    </div>
                  </div>
                </Tooltip>
              </CircleMarker>
            </>
          );
        })()}

        <MapController
          center={options.center}
          zoom={options.zoom}
          eventHandlers={eventHandlers}
          userInteractedRef={userInteractedRef}
          isInitializedRef={isInitializedRef}
          forceCenterRevision={forceCenterRevision}
        />

        <GeolocationControl onLocationFound={handleLocationFound} />
        {(userLocationProp || currentLocation) && (
          <>
            <Circle
              center={[
                (userLocationProp || currentLocation)!.lat,
                (userLocationProp || currentLocation)!.lng,
              ]}
              radius={120}
              pathOptions={{
                color: '#38bdf8',
                fillColor: '#38bdf8',
                fillOpacity: 0.15,
                weight: 2,
                opacity: 0.9,
              }}
            />
            <CircleMarker
              center={[
                (userLocationProp || currentLocation)!.lat,
                (userLocationProp || currentLocation)!.lng,
              ]}
              radius={6}
              pathOptions={{
                color: '#0ea5e9',
                fillColor: '#0ea5e9',
                fillOpacity: 1,
              }}
            />
          </>
        )}
      </MapContainer>
      {overlayNode && (
        <div className="absolute inset-0 pointer-events-none z-40">
          <div className="pointer-events-auto">{overlayNode}</div>
        </div>
      )}
    </div>
  );
});

export default LeafletMap;
